import { brl } from "@/lib/api";
import { MessageCircle, RefreshCw, ShoppingCart } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { Button, DataTable, PageHeader } from "@/components/ui-kit";

function relTime(date) {
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora mesmo";
  if (min < 60) return `há ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `há ${hrs} h`;
  const days = Math.floor(hrs / 24);
  return `há ${days} ${days === 1 ? "dia" : "dias"}`;
}

export default function Abandoned() {
  // Carrinhos vazios agora são descartados no banco, então a contagem da
  // paginação bate com o que aparece na tela.
  const {
    items: carts, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: retry, isFetching,
  } = usePaginatedList("/admin/abandoned-carts");

  return (
    <div className="space-y-5" data-testid="admin-abandoned-page">
      <PageHeader
        eyebrow="Operação"
        title="Carrinhos Abandonados"
        icon={ShoppingCart}
        description="Clientes logados que montaram o carrinho e não fecharam. O WhatsApp abre a conversa com a mensagem de recuperação pronta; quem não tem telefone no cadastro fica sem contato possível."
        // `loading` é o único retorno que o botão tem: a lista continua na tela e
        // o react-query mantém os dados antigos durante o refetch, então sem o
        // spinner o clique parece não ter feito nada.
        actions={(
          <Button variant="secondary" icon={RefreshCw} loading={isFetching} onClick={() => retry()} data-testid="abandoned-refresh-btn">
            Atualizar
          </Button>
        )}
      />

      <DataTable
        rowTestId="abandoned-row"
        caption="Carrinhos abandonados por cliente"
        rows={carts}
        rowKey={(c) => c.id}
        loading={loading}
        error={error}
        onRetry={retry}
        empty={{
          icon: ShoppingCart,
          title: "Nenhum carrinho abandonado",
          desc: "Os carrinhos de clientes logados aparecem aqui enquanto não viram pedido.",
          /* Esta lista não tem filtro nem cadastro — quem a alimenta é a loja.
             A única saída honesta do vazio é recarregar depois. */
          action: <Button variant="secondary" icon={RefreshCw} loading={isFetching} onClick={() => retry()}>Atualizar</Button>,
          testId: "abandoned-empty",
        }}
        columns={[
          {
            key: "cliente", header: "Cliente",
            cell: (c) => (
              <>
                <p className="font-medium text-slate-900">{c.name}</p>
                {c.email && <p className="text-xs text-slate-400">{c.email}</p>}
              </>
            ),
          },
          {
            key: "itens", header: "Itens no carrinho", className: "text-slate-600 text-xs",
            cell: (c) => c.items.map((i) => `${i.qty}x ${i.name}`).join(", "),
          },
          {
            key: "valor", header: "Valor", align: "right", mono: true, className: "font-semibold",
            cell: (c) => brl(c.total),
          },
          {
            key: "atividade", header: "Última atividade",
            cell: (c) => (
              <>
                {/* Âmbar é atenção, não status: quanto mais antigo o carrinho,
                    menor a chance de recuperar a venda. */}
                <span className="text-amber-600 font-medium">{relTime(c.updated_at)}</span>
                <p className="text-xs text-slate-400">{new Date(c.updated_at).toLocaleString("pt-BR")}</p>
              </>
            ),
          },
          {
            /* Fica como coluna comum (e não `acoes`): no cartão do celular o
               rodapé de ações força alvos quadrados de 44px, o que cortaria o
               rótulo "WhatsApp" e deixaria só um quadrado verde sem texto.
               A altura de toque vem por CSS: `h-11` abaixo de md (onde o
               DataTable vira cartão) atende os 44px do WCAG 2.5.5, e `md:h-8`
               devolve a altura de linha de tabela no desktop. */
            key: "reengajar", header: "Reengajar", align: "center",
            cell: (c) => (c.wa_link ? (
              <a
                href={c.wa_link} target="_blank" rel="noreferrer" data-testid="abandoned-whatsapp-btn"
                aria-label={`Chamar ${c.name} no WhatsApp`} title="Abrir conversa com a mensagem de recuperação"
                className="inline-flex items-center gap-1.5 h-11 md:h-8 px-3 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                <MessageCircle size={14} aria-hidden /> WhatsApp
              </a>
            ) : <span className="text-xs text-slate-400">sem telefone</span>),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="carrinhos" />
    </div>
  );
}
